import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useBusiness } from '../contexts/BusinessContext';
import {
  pendingAppUpdate,
  markAppUpdateSeen,
  type AppUpdate,
  type AppUpdatePlatform,
} from '../lib/appUpdates';

interface AppUpdateModalProps {
  /** Open this exact update, ignoring platform and whether it was already seen. */
  replay?: AppUpdate | null;
  onClose?: () => void;
}

function currentPlatform(): AppUpdatePlatform {
  if (!Capacitor.isNativePlatform()) return 'web';
  const platform = Capacitor.getPlatform();
  return platform === 'ios' || platform === 'android' ? platform : 'web';
}

function forcedByUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('show_update') === '1';
  } catch {
    return false;
  }
}

/**
 * The update itself: looping video on top, then the slides.
 * The launch popup and the What's new archive both render this.
 */
export function AppUpdatePost({
  update,
  onDone,
  autoFocusButton = false,
  titleId = 'app-update-title',
  compact = false,
  onOpen,
}: {
  update: AppUpdate;
  /** Called from the last slide. The archive leaves this off and restarts the slides. */
  onDone?: () => void;
  autoFocusButton?: boolean;
  titleId?: string;
  /** Small card for the Nexus Connect archive. The launch popup stays full size. */
  compact?: boolean;
  /** What's new cards call this to open the full-size post. */
  onOpen?: () => void;
}) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [update.id]);

  const slide = update.slides[index] ?? update.slides[0];
  const last = index === update.slides.length - 1;

  const next = () => {
    if (!last) {
      setIndex((i) => i + 1);
      return;
    }
    if (onDone) onDone();
    else setIndex(0);
  };

  if (!slide) return null;

  return (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      } : undefined}
      className={`flex flex-col overflow-hidden bg-white ${compact ? 'w-[220px] rounded-2xl border border-gray-100 shadow-sm' : 'w-full max-w-md rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.18)]'} ${onOpen ? 'cursor-pointer transition-shadow hover:shadow-md' : ''}`}
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif",
      }}
    >
      {update.video ? (
        <div className="relative shrink-0 bg-gray-950">
          <video
            key={update.video}
            src={update.video}
            className={`mx-auto block w-full object-contain ${compact ? 'max-h-72' : ''}`}
            style={compact ? undefined : { maxHeight: '48dvh' }}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            disablePictureInPicture
            aria-hidden
          />
        </div>
      ) : null}

      <div className={`flex min-h-0 flex-1 flex-col ${compact ? 'px-3 pb-3 pt-2.5' : 'px-6 pb-5 pt-5'}`}>
        <p className={`font-semibold uppercase tracking-[0.1em] text-blue-600 ${compact ? 'text-[9px]' : 'text-[11px]'}`}>{update.eyebrow}</p>
        <h2 id={titleId} className={`font-semibold leading-tight tracking-tight text-gray-900 ${compact ? 'mt-1 text-[13px]' : 'mt-1.5 text-[22px]'}`}>
          {slide.title}
        </h2>
        <p className={`leading-snug text-gray-600 ${compact ? 'mt-1 line-clamp-3 text-[11px]' : 'mt-2 min-h-[4.5rem] text-[15px] leading-relaxed'}`}>{slide.body}</p>

        <div className={`flex items-center justify-between gap-2 ${compact ? 'mt-2.5' : 'mt-5 gap-4'}`}>
          <div className="flex items-center gap-1" aria-label={`Step ${index + 1} of ${update.slides.length}`}>
            {update.slides.map((_, i) => (
              <span
                key={i}
                className={`rounded-full transition-all ${compact ? 'h-1' : 'h-1.5'} ${i === index ? (compact ? 'w-3 bg-gray-900' : 'w-5 bg-gray-900') : (compact ? 'w-1 bg-gray-300' : 'w-1.5 bg-gray-300')}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              if (onOpen) onOpen();
              else next();
            }}
            autoFocus={autoFocusButton}
            className={`rounded-full bg-gray-900 font-semibold text-white transition-colors hover:bg-black active:scale-[0.98] touch-manipulation ${compact ? 'px-2.5 py-1 text-[11px]' : 'px-6 py-2.5 text-[15px]'}`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            {last ? update.doneLabel : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Blocking "what's new" popup shown once per device after an update.
 * Nothing behind it is reachable until the user clicks through the slides.
 */
export default function AppUpdateModal({ replay = null, onClose }: AppUpdateModalProps = {}) {
  const { selectedBusiness } = useBusiness();
  const [dismissed, setDismissed] = useState(false);

  const update = useMemo(() => {
    if (replay) return replay;
    if (!selectedBusiness?.id) return null;
    return pendingAppUpdate(currentPlatform(), forcedByUrl());
  }, [replay, selectedBusiness?.id]);

  useEffect(() => {
    setDismissed(false);
  }, [update?.id]);

  const open = Boolean(update) && !dismissed;

  // Keep the page from scrolling underneath while the popup is up.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open || !update) return null;

  const finish = () => {
    markAppUpdateSeen(update.id);
    setDismissed(true);
    onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-update-title"
      onClick={replay ? () => onClose?.() : undefined}
    >
      <div className="w-full max-w-md" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} onClick={(event) => event.stopPropagation()}>
        <AppUpdatePost update={update} onDone={finish} autoFocusButton titleId="app-update-title" />
      </div>
    </div>,
    document.body,
  );
}
